import { useCallback, useEffect, useState, ReactNode } from 'react'

import useCallbackRef from 'app/common/hooks/useCallbackRef'

import PlainDropdown from './plain-dropdown'
import PlainDropdownDefitem from './plain-dropdown-defitem'

type Item = { ID: string; title: string } | { id: string; title: string }

const getID = (item: Item) => {
  if ('ID' in item) {
    return item.ID
  }
  return item.id
}

type Props<T extends Item> = {
  id: string
  labelId?: string
  className?: string
  itemsContainerClassName?: string
  value?: string
  defaultValue?: string | null
  items: Array<T>
  disabled?: boolean
  loading?: boolean
  keepSelectedElementInList?: boolean
  onChange?: (ID: string, controlId: string) => void | Promise<void>
  error?: boolean
  placeholderText?: string
  placeholder?: ReactNode
}

export function PlainDropdownSimplified<T extends Item>({
  id: controlId,
  labelId,
  className,
  itemsContainerClassName,
  value,
  defaultValue = null,
  items,
  disabled,
  loading,
  keepSelectedElementInList,
  onChange,
  error,
  placeholderText = 'Не указано',
  placeholder,
}: Props<T>) {
  const [currentValue, setCurrentValue] = useState(defaultValue)

  const onChangeRef = useCallbackRef(onChange)

  const handleChange = useCallback(
    ({ id: ID }: { id: string }) => {
      setCurrentValue(ID)
      onChangeRef.current?.(ID, controlId)
    },
    [onChangeRef, controlId],
  )

  useEffect(() => {
    if (value !== undefined) {
      setCurrentValue(value || '')
    }
  }, [value, loading])

  useEffect(() => {
    setCurrentValue(defaultValue || '')
  }, [defaultValue])

  return (
    <PlainDropdown
      id={controlId}
      labelId={labelId}
      className={className}
      itemsContainerClassName={itemsContainerClassName}
      onChange={handleChange}
      placeholder={
        placeholder || (
          <PlainDropdownDefitem id="placeholder">
            {placeholderText || '\u00A0'}
          </PlainDropdownDefitem>
        )
      }
      disabled={disabled}
      loading={loading}
      keepSelectedElementInList={keepSelectedElementInList}
      errored={error}
      defaultSkin
    >
      {items.map((props: T) => {
        const id = getID(props)
        const { title } = props

        return (
          <PlainDropdownDefitem
            id={id}
            key={id}
            disabled={disabled}
            selected={value ? id === value : id === currentValue}
          >
            {title}
          </PlainDropdownDefitem>
        )
      })}
    </PlainDropdown>
  )
}
